import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Api } from "services/api/api.service";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import type {
  EditingTab,
  EmploymentDetailsForm,
  EmploymentSaveMode,
} from "../profilePanel.contract";
import {
  applyEmploymentTemplateToEmploymentDetails,
  toVisibleTemplateSalaries,
} from "./profilePanel.state.template.logic";

interface UseProfilePanelTemplatesStateParams {
  companyId: number | null;
  editingTab: EditingTab;
  employmentSaveMode: EmploymentSaveMode;
  isOpen: boolean;
  setEditedEmploymentDetails: Dispatch<SetStateAction<EmploymentDetailsForm>>;
  setSaveError: (value: string) => void;
  workerAge: number | null;
  workerId: number | null;
}

interface UseProfilePanelTemplatesStateResult {
  employmentTemplates: EmploymentTemplateListItem[];
  isLoadingEmploymentTemplates: boolean;
  selectedEmploymentTemplateId: string;
  applyEmploymentTemplate: (templateId: string) => void;
  templateSalaries: EmploymentTemplateSalary[];
  selectedTemplateSalaryId: string;
  applyEmploymentTemplateSalary: (salaryId: string) => void;
  resetTemplateRequests: () => void;
}

const EMPTY_TEMPLATE_SALARIES: EmploymentTemplateSalary[] = [];
const EMPTY_TEMPLATES: EmploymentTemplateListItem[] = [];

export const useProfilePanelTemplatesState = (
  params: UseProfilePanelTemplatesStateParams,
): UseProfilePanelTemplatesStateResult => {
  const [employmentTemplates, setEmploymentTemplates] =
    useState<EmploymentTemplateListItem[]>(EMPTY_TEMPLATES);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] =
    useState("");
  const [templateSalaries, setTemplateSalaries] = useState<
    EmploymentTemplateSalary[]
  >(EMPTY_TEMPLATE_SALARIES);
  const [selectedTemplateSalaryId, setSelectedTemplateSalaryId] = useState("");
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailRequestIdRef = useRef(0);

  const resetTemplateState = useCallback((): void => {
    setEmploymentTemplates(EMPTY_TEMPLATES);
    setIsLoadingEmploymentTemplates(false);
    setSelectedEmploymentTemplateId("");
    setTemplateSalaries(EMPTY_TEMPLATE_SALARIES);
    setSelectedTemplateSalaryId("");
  }, []);

  const resetTemplateRequests = useCallback((): void => {
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailRequestIdRef.current += 1;
    resetTemplateState();
  }, [resetTemplateState]);

  const visibleTemplateSalaries = useMemo(() => {
    return toVisibleTemplateSalaries(templateSalaries, params.workerAge);
  }, [params.workerAge, templateSalaries]);

  useEffect(() => {
    if (!params.isOpen) {
      resetTemplateRequests();
      return;
    }

    const isCreateEmploymentFlow =
      params.editingTab === "employmentSalary" &&
      params.employmentSaveMode === "create";

    if (!isCreateEmploymentFlow) {
      resetTemplateRequests();
      return;
    }

    if (params.companyId == null) return;

    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    resetTemplateState();
    setIsLoadingEmploymentTemplates(true);

    void Api()
      .company.staff.getEmploymentTemplates(params.companyId)
      .then((response) => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
        setEmploymentTemplates(response.data ?? EMPTY_TEMPLATES);
      })
      .catch(() => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
      })
      .finally(() => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
        setIsLoadingEmploymentTemplates(false);
      });
  }, [
    params.companyId,
    params.editingTab,
    params.employmentSaveMode,
    params.isOpen,
    params.workerId,
    resetTemplateRequests,
    resetTemplateState,
  ]);

  const applyEmploymentTemplate = useCallback(
    (templateId: string): void => {
      setSelectedEmploymentTemplateId(templateId);
      setSelectedTemplateSalaryId("");
      setTemplateSalaries(EMPTY_TEMPLATE_SALARIES);
      params.setSaveError("");

      if (params.companyId == null || templateId.trim().length === 0) return;

      const requestId = employmentTemplateDetailRequestIdRef.current + 1;
      employmentTemplateDetailRequestIdRef.current = requestId;

      void Api()
        .company.staff.getEmploymentTemplate(
          params.companyId,
          Number(templateId),
        )
        .then((response) => {
          if (employmentTemplateDetailRequestIdRef.current !== requestId) {
            return;
          }

          const template = response.data;
          if (template == null) return;

          params.setEditedEmploymentDetails((previousValue) =>
            applyEmploymentTemplateToEmploymentDetails(previousValue, template),
          );
          setTemplateSalaries(template.salaries ?? EMPTY_TEMPLATE_SALARIES);
        })
        .catch(() => {
          if (employmentTemplateDetailRequestIdRef.current !== requestId) {
            return;
          }
        });
    },
    [params],
  );

  const applyEmploymentTemplateSalary = useCallback(
    (salaryId: string): void => {
      setSelectedTemplateSalaryId(salaryId);
      const selectedSalary = visibleTemplateSalaries.find(
        (salary) => String(salary.id) === salaryId,
      );
      if (selectedSalary == null) return;

      params.setEditedEmploymentDetails((previousValue) => ({
        ...previousValue,
        salary: String(selectedSalary.value),
      }));
      params.setSaveError("");
    },
    [params, visibleTemplateSalaries],
  );

  return {
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
    resetTemplateRequests,
  };
};
